import * as React from 'react';
import { SimpleGrid } from '@chakra-ui/react';
import { TrendingIconUpSVG } from '../CustomSVGs/TrendingIconUpSVG';
import {
  Heading,
  HStack,
  VStack,
} from '@ayble-health/dashboard-components';
import { Section } from './Section';
import { Tile } from './Tile';
import { Callout } from './Callout';
import { ChangeBadge } from './ChangeBadge';
import { BlockCardAudit } from './BlockCardAudit';
import { StatusPill } from '../ProviderHome/StatusPill';
import { OverviewCard } from '../UserDashboard/overview/OverviewCard';
import { Pill } from '../UserDashboard/Pill';
import type { VisitStatus } from '../../models/providerHome';

const VISIT_STATUSES: VisitStatus[] = [
  'confirmed',
  'unconfirmed',
  'rescheduled',
  'late_cancel',
  'occurred',
  'no_show',
  'cancelled',
];

/**
 * The status→Chip mapping, not a second pill set.
 *
 * This used to render the full 7 statuses × 2 sizes grid, which documented a
 * pill vocabulary that was Chip's vocabulary under different names — two
 * specimens of one object, free to drift. `StatusPill` now composes `Chip`, so
 * the chrome is documented once on the Atoms tab and what is left here is the
 * only part that is genuinely this component's: which status maps to which
 * variant, and the three statuses that are not plain labels.
 */
const StatusVocabulary = () => (
  <VStack gap={4} align="stretch">
    <HStack gap={3} wrap="wrap" align="center">
      {VISIT_STATUSES.map((status) => (
        <StatusPill
          key={status}
          status={status}
          rescheduledTo={
            status === 'rescheduled' ? '2026-07-14T15:30:00Z' : undefined
          }
        />
      ))}
    </HStack>

    <Callout title="The chrome is Chip — this is the status vocabulary on top of it">
      Every pill above is a <code>Chip</code>. <code>confirmed</code> is{' '}
      <code>aqua</code>, <code>unconfirmed</code> is <code>symptom</code>, and
      the four lapsed statuses are <code>alert</code> — a fuschia outline added
      to Chip because <code>StatusPill</code> had been hand-rolling it. The
      variant grid lives on the Atoms tab; duplicating it here is what let two
      names for one pill sit side by side for months.
      <br />
      <br />
      <strong>Three statuses are not just a variant, which is why this
      component still exists.</strong> <code>occurred</code> renders no pill at
      all — bare muted text, an anatomy change a variant cannot express.{' '}
      <code>unconfirmed</code> is a control: clicking it confirms the
      appointment, and Chip is deliberately inert. <code>rescheduled</code>{' '}
      appends a formatted slot time inside the pill, so its label is two nodes.
      Two of the three are behaviour, and behaviour is not skinnable.
    </Callout>

  </VStack>
);

const FeatureMolecules = () => (
  <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
    <Tile label="OverviewCard — patient profile stat tile">
      <OverviewCard
        title="Symptom change"
        text="-32%"
        borderColor="cloud"
        w="100%"
        icon={<TrendingIconUpSVG />}
        bottomTag={{ text: 'vs. last 30 days' }}
      />
    </Tile>
    <Tile label="Pill — diet / allergen tag">
      <HStack gap={0} wrap="wrap">
        <Pill label="Dairy" />
        <Pill label="Gluten" />
        <Pill label="Low FODMAP" />
      </HStack>
    </Tile>
  </SimpleGrid>
);

/**
 * Recurring composite patterns found across the homepage, patient profile,
 * and labs experiences that are NOT in ui/ — each is a feature-local
 * component that repeats. Referenced (not rendered live) because they're
 * bound to domain data; this is the "what exists and where" index a
 * designer needs before building something new.
 */
export const FeatureAudit = () => (
  <Section
    id="patterns"
    title="Patterns in the wild"
    description="Audited from the homepage, patient profile, and labs experiences — the
      canonical status badge, feature molecules, and composite patterns that
      make up the real design system beyond the ui/ wrappers."
  >
    <VStack gap={8} align="stretch">
      <VStack gap={2} align="stretch">
        <HStack gap={2} align="center" wrap="wrap">
          <Heading size="md">Status vocabulary — StatusPill</Heading>
          <ChangeBadge kind="updated" date="Aug 13, 2026" />
        </HStack>
        <StatusVocabulary />
      </VStack>

      <BlockCardAudit />

      <VStack gap={2} align="stretch">
        <Heading size="md">Feature molecules</Heading>
        <FeatureMolecules />
      </VStack>

    </VStack>
  </Section>
);
